import Errors from './Errors';
import _ from 'lodash';
import { deepCopy, hasFile } from './utils';
import { toFormData } from './FormData';

/**
 * Declare private methods
 */
const initFormData = Symbol('initFormData');
const mergeAppends = Symbol('mergeAppends');

class Form {
    /**
     * Initialize the class.
     *
     * @param {Object} data
     */
    constructor (data) {
        // Initialize the form data.
        this[initFormData](data);

        // Setup default values for class variables.
        this.setClassVariables();

        // Initialize the error bag.
        this.errors = new Errors();
    }

    /**
     * Initialize the form's data.
     *
     * @param {Object} data
     */
    [initFormData] (data) {
        this.model = data;
        this.originalData = deepCopy(data);
    }

    /**
     * Merge the form data with the data that should get appended to the request.
     *
     * @param {Object} data
     * @param {Boolean} transform
     */
    [mergeAppends] (data, transform) {
        if (_.isNil(this.overriddenData) && ! _.isNil(this.appends) && ! _.isEmpty(this.appends)) {
            data = _.merge(this.appends, data);
        }

        if (transform) {
            if (this.forceFormData || hasFile(data)) {
                data = toFormData(data);
            }

            if (this.stringify) {
                const qs = require('qs');
                data = qs.stringify(data);
            }
        }

        return data;
    }

    /**
     * Reset the form to its original state.
     */
    reset () {
        if (! this.destroyOnReset) {
            for (let field in this.originalData) {
                this.model[field] = this.originalData[field];
            }
        } else {
            for (let field in this.model) {
                if (_.isBoolean(this.model[field])) {
                    this.model[field] = false;
                } else if (_.isArray(this.model[field])) {
                    this.model[field] = [];
                } else if (_.isObject(this.model[field])) {
                    this.model[field] = {};
                } else {
                    this.model[field] = '';
                }
            }
        }
    }

    /**
     * Set defaults for class variables.
     */
    setClassVariables () {
        this.form = null;
        this.action = null;
        this.destroyOnReset = false;
        this.props = null;
        this.appends = {};
        this.stringify = false;
        this.notifyOnFail = true;
        this.notifyOnSuccess = true;
        this.overriddenData = null;
        this.forceFormData = false;
    }

    forceFormDataSubmission () {
        this.forceFormData = true;

        return this;
    }

    /**
     * Set the endpoint the form is supposed to submit to.
     *
     * @param {String} action
     */
    setAction (action) {
        this.action = action;
        return this;
    }

    /**
     * Set custom data to be sent with the request.
     *
     * @param {Object} data
     */
    setOverridenData (data) {
        this.overriddenData = data;
    }

    /**
     * Set data that should be appended to the request.
     *
     * @param {Object} appends
     */
    setAppends (appends) {
        this.appends = appends;
        return this;
    }

    /**
     * Update appended values.
     *
     * @param {String} key
     * @param {mixed} value
     */
    updateAppends (key, value) {
        this.appends[key] = value;
    }

    /**
     * Determines if the data should be reset to original state or completely destroyed.
     *
     * @param {Boolean} destroy
     */
    setDestroyOnReset (destroy = true) {
        this.destroyOnReset = destroy;
        return this;
    }

    /**
     * Set whether or not to notify the user of a failed request from this class.
     *
     * @param {Boolean} notify
     */
    setNotifyOnFail (notify = false) {
        this.notifyOnFail = notify;
        return this;
    }

    /**
     * Set whether or not to notify the user of a successful request from this class.
     *
     * @param {Boolean} notify
     */
    setNotifyOnSuccess (notify = false) {
        this.notifyOnSuccess = notify;
        return this;
    }

    /**
     * Set the properties of the model that should be sent to the server.
     *
     * @param {Array} props
     */
    setProps (props) {
        this.props = props;
        return this;
    }

    /**
     * Set data to be stringified when sent to the server.
     *
     * @param {Boolean} enable
     */
    setStringify (enable = true) {
        this.stringify = enable;
        return this;
    }

    /**
     * Set the form to an html element.
     *
     * @param form
     */
    setForm (form) {
        this.form = form;

        try {
            let action = form.getAttribute('action');
            if (! _.isNil(action) && action.trim() !== '') {
                this.action = action;
            }
        } catch (e) {}

        return this;
    }

    /**
     * Update the value of a field.
     *
     * @param {String} field
     * @param {mixed} value
     * @param {Boolean} updateOriginal
     */
    updateField (field, value, updateOriginal = false) {
        this.model[field] = value;

        if (updateOriginal) {
            this.originalData[field] = value;
        }

        return this;
    }

    /**
     * Update the current model.
     *
     * @param {Object} newModel
     */
    updateModel (newModel) {
        this[initFormData](newModel);
        return this;
    }

    /**
     * Get the form data that should be sent to the server.
     *
     * @param {Boolean} transform
     */
    data (transform = false) {
        let data = {};
        if (_.isNil(this.overriddenData)) {
            if (_.isNil(this.props) || _.isEmpty(this.props)) {
                for (let prop in this.model) {
                    data[prop] = this.model[prop];
                }
            } else {
                if (_.isArray(this.props)) {
                    this.props.forEach(prop => {
                        data[prop] = this.model[prop];
                    });
                }
            }
        } else {
            data = this.overriddenData;
        }

        return this[mergeAppends](data, transform);
    }

    /**
     * Submit a GET request to the server.
     *
     * @param {String} endPoint
     */
    get (endPoint = null) {
        return this.submit('get', this.getAction(endPoint));
    }

    /**
     * Submit a PATCH request to the server.
     *
     * @param {String} endPoint
     */
    patch (endPoint = null) {
        this.submit('patch', this.getAction(endPoint));
    }

    /**
     * Submit a POST request to the server.
     *
     * @param {String} endPoint
     */
    post (endPoint = null) {
        return this.submit('post', this.getAction(endPoint));
    }

    /**
     * Submit a PUT request to the server.
     *
     * @param {String} endPoint
     */
    put (endPoint = null) {
        return this.submit('put', this.getAction(endPoint));
    }

    /**
     * Get the action for the current request.
     *
     * @param {String} endPoint
     */
    getAction (endPoint = null) {
        if (_.isNil(endPoint) || endPoint.trim() === '') {
            endPoint = this.action;
        }

        return endPoint;
    }

    /**
     * Submit an ajax request.
     *
     * @param {String} requestType
     * @param {String} endPoint
     */
    submit (requestType, endPoint) {
        let data = this.data(true);

        if (requestType.toLowerCase() === 'get') {
            data = {
                params: data
            };
        }

        // Reset the form errors.
        this.errors.clear();

        // Submit the request, return as a promise.
        return new Promise((resolve, reject) => {
            axios[requestType](endPoint, data)
                .then(({ data }) => {
                    if (data.error === true && data.errors) {
                        this.errors.record(data.errors);
                    }

                    if (! data.error && data.message && this.notifyOnSuccess) {
                        showResponse(data.message, 'success');
                    }

                    resolve(data);
                })
                .catch(error => {
                    let err;
                    if (error.response) {
                        err = {
                            data: error.response.data,
                            status: error.response.status,
                            headers: error.response.headers
                        };

                        if (error.response.data.errors) {
                            // A validation exception was thrown
                            this.errors.record(error.response.data.errors);
                        } else {
                            if (this.notifyOnFail) {
                                showResponse(this.errorMessage(err), 'error');
                            }
                        }
                    } else if (error.request) {
                        err = error.request;
                    } else {
                        err = error;
                    }

                    reject(err);
                });
        });
    }

    /**
     * Get the appropriate error message from a failed request.
     *
     * @param {Object} error
     */
    errorMessage (error) {
        const defaultMessage = 'We were unable to process your request. Please try again later.';
        if (error.data.error) {
            return error.data.message ? error.data.message : defaultMessage;
        }

        return defaultMessage;
    }
}

export default Form;